import React, { useState, useEffect } from "react";
import { LangContext } from "../../contexts/LangContext";
import en from "../../i18n/en";
import pl from "../../i18n/pl";

// Загружаем темплейты
let FILTERING_TEMPLATES = {};
try {
  const requireContext = require.context(
    "../../filtering_templates",
    false,
    /\.json$/
  );
  
  requireContext.keys().forEach((filename) => {
    const templateName = filename.replace("./", "").replace(".json", "");
    FILTERING_TEMPLATES[templateName] = requireContext(filename);
  });
} catch (e) {
  console.warn("Could not load filtering templates:", e);
}

const STORAGE_KEY = "create_filtering_section_data";

export default function FilteringSection({ selectedPlatform }) {
  const { lang } = React.useContext(LangContext);
  const t = lang === "pl" ? pl : en;

  const getInitialState = () => {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {}
    }
    return {
      filters: {},
      useAI: false,
      aiGoal: "",
      aiPrompt: "",
      matchWord: "",
      matchScope: "full_text",
    };
  };

  const [state, setState] = useState(getInitialState);

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  useEffect(() => {
    const handleClear = () => setState(getInitialState());
    window.addEventListener("clearAllTasks", handleClear);
    return () => window.removeEventListener("clearAllTasks", handleClear);
  }, []);

  // Загружаем темплейт при изменении платформы
  useEffect(() => {
    if (selectedPlatform) {
      const templateKey = `${selectedPlatform}_post`;
      const template = FILTERING_TEMPLATES[templateKey];
      
      if (template?.filters && Object.keys(state.filters).length === 0) {
        const initialFilters = {};
        Object.entries(template.filters).forEach(([key, config]) => {
          if (config.type === "checkbox") {
            initialFilters[key] = config.default || false;
          } else if (config.type === "number") {
            initialFilters[key] = config.default !== undefined ? config.default : "";
          } else {
            initialFilters[key] = "";
          }
        });
        setState(prev => ({ ...prev, filters: initialFilters }));
      }
    }
  }, [selectedPlatform]);

  const updateState = (key, value) => {
    setState(prev => ({ ...prev, [key]: value }));
  };

  const updateFilter = (key, value) => {
    setState(prev => ({
      ...prev,
      filters: { ...prev.filters, [key]: value }
    }));
  };

  // Получаем текущий темплейт
  const getCurrentTemplate = () => {
    if (!selectedPlatform) return null;
    const templateKey = `${selectedPlatform}_post`;
    return FILTERING_TEMPLATES[templateKey] || null;
  };

  const getAvailablePlaceholders = () => {
    const template = getCurrentTemplate();
    if (!template?.filters) return [];
    
    return Object.keys(template.filters).map(key => ({
      key: key.toUpperCase(),
      label: template.filters[key].label || key
    }));
  };

  const insertPlaceholder = (placeholder) => {
    const textarea = document.getElementById("aiPromptTextareaFiltering");
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = state?.aiPrompt || "";
      const before = text.substring(0, start);
      const after = text.substring(end, text.length);
      const newText = before + `[[${placeholder}]]` + after;
      updateState("aiPrompt", newText);
      
      setTimeout(() => {
        textarea.focus();
        textarea.selectionStart = start + placeholder.length + 4;
        textarea.selectionEnd = start + placeholder.length + 4;
      }, 0);
    }
  };

  const generateAIPrompt = () => {
    const goal = state?.aiGoal || "";
    if (!goal.trim()) return;
    
    const template = getCurrentTemplate();
    let prompt = `Task: ${goal.trim()}\n\n`;
    prompt += "Filtering criteria:\n\n";
    
    if (template?.filters) {
      Object.entries(template.filters).forEach(([key, config]) => {
        const value = state?.filters?.[key];
        if (value && value !== "" && value !== false) {
          prompt += `- ${config.label}: [[${key.toUpperCase()}]]\n`;
        }
      });
    }
    
    const matchWord = state?.matchWord || "";
    if (matchWord.trim()) {
      prompt += `\nMatch condition: Post passes filter if it contains "${matchWord.trim()}"`;
      prompt += state?.matchScope === "first_line" ? " in the FIRST LINE" : " ANYWHERE in text";
    }
    
    updateState("aiPrompt", prompt);
  };

  const currentTemplate = getCurrentTemplate();
  const placeholders = getAvailablePlaceholders();
  const filters = state?.filters || {};
  const useAI = state?.useAI || false;
  const aiGoal = state?.aiGoal || "";
  const aiPrompt = state?.aiPrompt || "";
  const matchWord = state?.matchWord || "";
  const matchScope = state?.matchScope || "full_text";

  // Экспортируем данные для сохранения
  useEffect(() => {
    window.filteringSectionData = {
      filters: state.filters,
      useAI: state.useAI,
      aiGoal: state.aiGoal,
      aiPrompt: state.aiPrompt,
      matchWord: state.matchWord,
      matchScope: state.matchScope,
    };
  }, [state]);

  return (
    <>
      {/* Filters */}
      {currentTemplate?.filters && (
        <div className="section-card">
          <h3>🔧 {t.additionalFilters}</h3>
          <p style={{ fontSize: 12, color: "var(--muted)", marginBottom: 12 }}>
            {currentTemplate.name}
          </p>
          
          <div style={{ display: "grid", gap: 12 }}>
            {Object.entries(currentTemplate.filters).map(([key, config]) => {
              if (config.type === "text_list") {
                return (
                  <div key={key}>
                    <label style={{ fontWeight: 600, fontSize: 13, display: "block", marginBottom: 4 }}>
                      {config.label}
                    </label>
                    <textarea
                      className="intent-textarea"
                      placeholder={config.placeholder}
                      value={filters[key] || ""}
                      onChange={(e) => updateFilter(key, e.target.value)}
                      style={{ minHeight: 80, width: "100%" }}
                    />
                    {config.help && (
                      <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>{config.help}</div>
                    )}
                  </div>
                );
              }
              
              if (config.type === "number") {
                return (
                  <div key={key}>
                    <label style={{ fontWeight: 600, fontSize: 13, display: "block", marginBottom: 4 }}>
                      {config.label}
                    </label>
                    <input
                      className="form-input"
                      type="number"
                      placeholder={config.placeholder}
                      value={filters[key] !== undefined ? filters[key] : ""}
                      onChange={(e) => updateFilter(key, e.target.value)}
                      style={{ width: "100%" }}
                    />
                  </div>
                );
              }
              
              if (config.type === "checkbox") {
                return (
                  <div key={key}>
                    <label style={{ 
                      display: "flex", 
                      alignItems: "center", 
                      gap: 8, 
                      cursor: "pointer",
                      padding: "8px 0"
                    }}>
                      <input
                        type="checkbox"
                        checked={filters[key] || false}
                        onChange={(e) => updateFilter(key, e.target.checked)}
                      />
                      <span style={{ fontSize: 13 }}>{config.label}</span>
                    </label>
                  </div>
                );
              }
              
              return null;
            })}
          </div>
        </div>
      )}

      {/* AI Filtering */}
      <div className="section-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3>🤖 {t.aiFiltering}</h3>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={useAI}
              onChange={(e) => updateState("useAI", e.target.checked)}
            />
            <span style={{ fontSize: 13, fontWeight: 600 }}>{t.enable}</span>
          </label>
        </div>

        {useAI && (
          <div style={{ marginTop: 12 }}>
            {/* AI Goal + Generate Button */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontWeight: 600, fontSize: 13, display: "block", marginBottom: 4 }}>
                🎯 {t.whatResult}
              </label>
              <textarea
                className="intent-textarea"
                placeholder={t.describeFiltering}
                value={aiGoal}
                onChange={(e) => updateState("aiGoal", e.target.value)}
                style={{ minHeight: 80, width: "100%", marginBottom: 8 }}
              />
              <button
                onClick={generateAIPrompt}
                disabled={!aiGoal?.trim()}
                style={{
                  width: "100%",
                  padding: "10px",
                  background: aiGoal?.trim() 
                    ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" 
                    : "#ccc",
                  color: "white",
                  border: "none",
                  borderRadius: 8,
                  cursor: aiGoal?.trim() ? "pointer" : "not-allowed",
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                🤖 {t.generateAIPrompt}
              </button>
            </div>

            {/* Available Placeholders */}
            {placeholders.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontWeight: 600, fontSize: 13, display: "block", marginBottom: 8 }}>
                  📌 {t.availablePlaceholders}
                </label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {placeholders.map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => insertPlaceholder(key)}
                      title={label}
                      style={{
                        padding: "6px 12px",
                        background: "#e8eaff",
                        border: "1px solid #b8bfff",
                        borderRadius: 4,
                        cursor: "pointer",
                        fontSize: 11,
                        fontFamily: "monospace",
                        fontWeight: 600,
                        color: "#4a4aff",
                        transition: ".2s",
                      }}
                    >
                      {`[[${key}]]`}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* AI Prompt */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontWeight: 600, fontSize: 13, display: "block", marginBottom: 4 }}>
                📝 {t.aiPrompt}
              </label>
              <textarea
                id="aiPromptTextareaFiltering"
                className="intent-textarea"
                placeholder={t.aiPromptPlaceholder}
                value={aiPrompt}
                onChange={(e) => updateState("aiPrompt", e.target.value)}
                style={{ minHeight: 120, width: "100%", fontFamily: "monospace", fontSize: 12 }}
              />
            </div>

            {/* Match Condition */}
            <div style={{ 
              padding: 12, 
              background: "#f0f4ff", 
              borderRadius: 8, 
              border: "1px solid #d0d7ff" 
            }}>
              <label style={{ fontWeight: 600, fontSize: 13, display: "block", marginBottom: 8 }}>
                🎯 {t.matchCondition}
              </label>
              <div style={{ display: "grid", gap: 12 }}>
                <div>
                  <label style={{ fontWeight: 500, fontSize: 12, display: "block", marginBottom: 4 }}>
                    {t.wordToMatch}
                  </label>
                  <input
                    className="form-input"
                    type="text"
                    placeholder={t.wordToMatchPlaceholder}
                    value={matchWord}
                    onChange={(e) => updateState("matchWord", e.target.value)}
                    style={{ width: "100%" }}
                  />
                </div>
                
                <div>
                  <label style={{ fontWeight: 500, fontSize: 12, display: "block", marginBottom: 4 }}>
                    {t.searchScope}
                  </label>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      onClick={() => updateState("matchScope", "full_text")}
                      style={{
                        flex: 1,
                        padding: "8px",
                        borderRadius: 6,
                        border: `2px solid ${matchScope === "full_text" ? "var(--blue)" : "var(--border)"}`,
                        background: matchScope === "full_text" ? "var(--blue-light)" : "#fff",
                        cursor: "pointer",
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      📄 {t.entireText}
                    </button>
                    <button
                      onClick={() => updateState("matchScope", "first_line")}
                      style={{
                        flex: 1,
                        padding: "8px",
                        borderRadius: 6,
                        border: `2px solid ${matchScope === "first_line" ? "var(--blue)" : "var(--border)"}`,
                        background: matchScope === "first_line" ? "var(--blue-light)" : "#fff",
                        cursor: "pointer",
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      📝 {t.firstLineOnly}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}